import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react'
import { MessageSquareQuote, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { type ChatAttachment, type ChatMode } from '@hub/contracts'
import {
  Composer,
  ComposerAttachmentChip,
  ComposerAttachments,
  ComposerBar,
  ComposerSend,
  ComposerVoice,
  ComposerVoiceButton,
  type ComposerAttachment,
} from '@/components/assistant-ui/elements/composer'
import { field, ghostButton } from '@/components/assistant-ui/elements/surfaces'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { shouldConfirmStop } from '@/lib/chat/slow-turn'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { AttachmentSourcePicker, type PickedDocument } from '@/components/attachment-source-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown, TriangleAlert } from 'lucide-react'
import {
  CHAT_DRAFT_RESTORED_EVENT,
  CHAT_DRAFT_SELECTIONS_EVENT,
  clearChatDraft,
  getChatDraft,
  getChatDraftAttachments,
  getChatDraftMode,
  getChatDraftSelections,
  setChatDraft,
  setChatDraftAttachments,
  setChatDraftMode,
  setChatDraftSelections,
  type ChatDraftRestoredDetail,
} from '@/lib/chat-draft-store'
import {
  parseDraftSelections,
  quoteSelections,
  selectionPreview,
  SELECTION_MAX_CHARS,
  type ChatSelection,
} from '@/lib/chat/selections'
import { isChatMode } from '@hub/contracts'
import { clipboardFiles } from '@/lib/chat/clipboard-files'
import { currentPlatformInfo, enterSends, sendShortcut } from '@/lib/chat/send-shortcut'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { dictationAvailable, useDictation } from '@/lib/use-dictation'
import { createDictationSubmit, joinText, type DictationSubmit } from '@/lib/chat/dictation-submit'
import { useLocale } from '@/i18n/locale'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { formatFileSize } from './time'

interface UploadingFile {
  id: string
  name: string
  size: number
  contentType: string
  progress: number
  status: 'uploading' | 'uploaded' | 'error'
  error?: string
  result?: ChatAttachment
}

export interface ChatComposerProps {
  /** The project the composer works in — uploads, attachments and the credit
   * charge for a dictation session all need it, so it is never optional. */
  projectId: string
  /**
   * Send the message. Resolving to `false` means the send was refused (out of
   * credits) — the composer puts the text and attachments back.
   */
  onSend: (
    message: string,
    attachments?: ChatAttachment[]
  ) => void | boolean | Promise<void | boolean>
  onTyping?: (isTyping: boolean) => void
  /** The agent is busy — sends are queued; the button shows the streaming state. */
  busy?: boolean
  /**
   * Stop the reply in progress. While busy the send button becomes a stop
   * control (⌘/Ctrl+Enter still queues the draft); resolves once the server settled
   * the turn, and the button shows a pending state meanwhile.
   */
  onStop?: () => Promise<void> | void
  /**
   * When the running turn started. A Stop pressed more than 20 s in asks for
   * confirmation first — a user once stopped a 12-minute run believing it was
   * dead, seconds before the answer landed (PLAN v2).
   */
  busySince?: string | null
  disabled?: boolean
  placeholder?: string
  autoFocus?: boolean
  /** When set, the textarea value persists across remounts in localStorage. */
  draftKey?: { projectId: string; threadKey: string }
  /** New chats always begin in Normal mode — set on the new-chat landing. */
  resetModeToNormal?: boolean
  className?: string
}

/** Which send shortcut to show — the platform cannot change under a running tab. */
const SEND_SHORTCUT = sendShortcut(currentPlatformInfo())

/** Composer input height cap: lines of text before it scrolls. */
const MAX_LINES = 5
const MOBILE_MAX_LINES = 5
/** Must match the textarea's `leading-6` / `py-2`. */
const LINE_HEIGHT_PX = 24
const TEXTAREA_PADDING_PX = 16

function attachmentKind(contentType: string): ComposerAttachment['kind'] {
  if (contentType.startsWith('image/')) return 'image'
  if (/zip|tar|gzip|compressed/.test(contentType)) return 'archive'
  return 'text'
}

/** Three-segment strength bar: Normal fills one, High two (neutral), Max all (yellow). */
function ModeBar({ mode }: { mode: ChatMode }) {
  const filled = mode === 'normal' ? 1 : mode === 'high' ? 2 : 3
  const fillClass = mode === 'max' ? 'bg-primary' : 'bg-foreground/60'
  return (
    <span aria-hidden className="flex items-center gap-0.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`h-1.5 w-3 rounded-full ${i < filled ? fillClass : 'bg-foreground/15'}`}
        />
      ))}
    </span>
  )
}

/** Restore a draft attachment as an already-uploaded composer chip. */
function draftAttachmentToFile(attachment: ChatAttachment): UploadingFile {
  return {
    id: attachment.id,
    name: attachment.filename,
    size: attachment.size,
    contentType: attachment.contentType,
    progress: 100,
    status: 'uploaded',
    result: attachment,
  }
}

/**
 * "N selections" chip above the input: opens a list of the selected excerpts
 * with a remove control per entry. Styled like the attachment chips.
 */
function SelectionsChip({
  selections,
  onRemove,
}: {
  selections: ChatSelection[]
  onRemove: (id: string) => void
}) {
  const { t } = useTranslation('chat')
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (selections.length === 0) setOpen(false)
  }, [selections.length])
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-slot="composer-selections"
          className={cn(
            field,
            'hover:bg-foreground/[0.08] flex items-center gap-2.5 rounded-[14px] py-1.5 ps-1.5 pe-3 text-left transition-colors'
          )}
        >
          <span className="bg-background flex size-8 shrink-0 items-center justify-center rounded-[10px] text-blue-500 dark:bg-white/10 dark:text-blue-400">
            <MessageSquareQuote className="size-4" />
          </span>
          <span className="flex flex-col">
            <span className="text-xs font-medium">
              {t('composer.selections', { count: selections.length })}
            </span>
            <span className="text-foreground/40 max-w-40 truncate text-[11px]">
              {selectionPreview(selections[0].text, 60)}
            </span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={8} className="w-[min(24rem,calc(100vw-2rem))] p-2">
        <p className="text-foreground/50 px-2 pt-1 pb-2 text-[11px] font-medium">
          {t('composer.selectionsTitle')}
        </p>
        <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
          {selections.map((selection, index) => (
            <li
              key={selection.id}
              data-slot="composer-selection"
              className={cn(field, 'flex items-start gap-2 rounded-xl py-2 ps-3 pe-1.5')}
            >
              <span className="text-foreground/35 w-4 shrink-0 pt-px text-[11px] tabular-nums">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className="line-clamp-4 text-[13px] leading-snug whitespace-pre-wrap">
                  {selection.text}
                </span>
                {selection.truncated && (
                  <span className="text-foreground/40 mt-1 block text-[11px]">
                    {t('composer.selectionTruncated', { max: SELECTION_MAX_CHARS })}
                  </span>
                )}
              </span>
              <button
                type="button"
                aria-label={t('composer.removeSelection')}
                onClick={() => onRemove(selection.id)}
                className={cn(ghostButton, 'size-6 shrink-0 [&_svg]:size-3.5')}
              >
                <X />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

/** Message input built on the assistant-ui composer element (multiline). */
export function ChatComposer({
  projectId,
  onSend,
  onTyping,
  busy = false,
  onStop,
  busySince,
  disabled = false,
  placeholder,
  autoFocus,
  draftKey,
  resetModeToNormal = false,
  className,
}: ChatComposerProps) {
  const { t } = useTranslation('chat')
  const [value, setValue] = useState(() =>
    draftKey ? getChatDraft(draftKey.projectId, draftKey.threadKey) : ''
  )
  const [files, setFiles] = useState<UploadingFile[]>(() =>
    draftKey
      ? getChatDraftAttachments<ChatAttachment>(draftKey.projectId, draftKey.threadKey).map(
          draftAttachmentToFile
        )
      : []
  )
  // Text selected in a reply and added with "Add to chat" — quoted above the
  // message on send. Lives in the draft store like the attachments.
  const [selections, setSelections] = useState<ChatSelection[]>(() =>
    draftKey
      ? parseDraftSelections(getChatDraftSelections(draftKey.projectId, draftKey.threadKey))
      : []
  )
  const [dragActive, setDragActive] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Dictation (the configured realtime STT provider, web/lib/dictation): the
  // text typed before pressing the mic is kept as a base; committed + live
  // partial transcript is appended to it.
  const { locale } = useLocale()
  const [dictationEnabled, setDictationEnabled] = useState(false)
  useEffect(() => {
    void dictationAvailable().then(setDictationEnabled)
  }, [])
  const dictationBaseRef = useRef('')
  // Both set below, once `dictation` exists — the hook's callbacks fire later.
  const dictationSubmitRef = useRef<DictationSubmit | null>(null)
  const keepDictationRef = useRef<() => void>(() => {})
  const dictation = useDictation({
    locale,
    projectId,
    onLimit: () => {
      toast.info(t('composer.dictationLimit'))
      keepDictationRef.current()
    },
    // Silence ran out with text on screen: the countdown ring on the send
    // button has completed — hand the session to the very submit a keypress
    // goes through, so the two of them can only send it once.
    onAutoStop: () => void dictationSubmitRef.current?.run(),
    onError: (err) =>
      toast.error(
        t(
          err.reason === 'unavailable'
            ? 'composer.dictationUnavailable'
            : err.reason === 'mic_denied'
              ? 'composer.micDenied'
              : err.reason === 'credits_exhausted'
                ? 'credits.exhaustedDictation'
                : 'composer.dictationFailed'
        )
      ),
  })
  const dictationStopRef = useRef(dictation.stop)
  dictationStopRef.current = dictation.stop
  useEffect(() => {
    if (dictation.status === 'idle') return
    const next = joinText(
      joinText(dictationBaseRef.current, dictation.committed),
      dictation.partial
    )
    setValue(next)
    onTyping?.(next.length > 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.committed, dictation.partial, dictation.status])
  /** End the session and leave its text in the composer (stop control, Esc, cap). */
  const stopDictation = useCallback(async () => {
    const text = await dictation.stop()
    // A submit got there first and owns the text — writing it back here would
    // put the message that was just sent into the next one.
    if (dictationSubmitRef.current?.pending()) return
    const next = joinText(dictationBaseRef.current, text)
    setValue(next)
    if (draftKey) setChatDraft(draftKey.projectId, draftKey.threadKey, next)
    textareaRef.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictation.stop, draftKey])
  keepDictationRef.current = () => void stopDictation()
  const startDictation = async () => {
    dictationBaseRef.current = value
    await dictation.start()
  }
  // Esc ends dictation from anywhere (the stop control sits in the voice row).
  useEffect(() => {
    if (!dictation.active) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        void stopDictation()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dictation.active, stopDictation])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const xhrs = useRef<Map<string, XMLHttpRequest>>(new Map())

  // Persist the attachment draft the same way the text draft is persisted:
  // at the moment the user changes something (upload finished, chip removed,
  // documents picked) — never from an effect. An effect observes stale state
  // while switching threads and leaked one thread's attachments into another.
  const draftKeyRef = useRef(draftKey)
  draftKeyRef.current = draftKey
  const mutateFiles = useCallback((updater: (prev: UploadingFile[]) => UploadingFile[]) => {
    setFiles((prev) => {
      const next = updater(prev)
      const dk = draftKeyRef.current
      if (dk) {
        setChatDraftAttachments(
          dk.projectId,
          dk.threadKey,
          next
            .filter((f) => f.status === 'uploaded' && f.result)
            .map((f) => f.result as ChatAttachment)
        )
      }
      return next
    })
  }, [])

  const mutateSelections = useCallback((updater: (prev: ChatSelection[]) => ChatSelection[]) => {
    setSelections((prev) => {
      const next = updater(prev)
      const dk = draftKeyRef.current
      if (dk) setChatDraftSelections(dk.projectId, dk.threadKey, next)
      return next
    })
  }, [])

  const draftProjectId = draftKey?.projectId
  const draftThreadKey = draftKey?.threadKey
  useEffect(() => {
    if (!draftProjectId || !draftThreadKey) return
    setValue(getChatDraft(draftProjectId, draftThreadKey))
    setFiles(
      getChatDraftAttachments<ChatAttachment>(draftProjectId, draftThreadKey).map(
        draftAttachmentToFile
      )
    )
    setSelections(parseDraftSelections(getChatDraftSelections(draftProjectId, draftThreadKey)))
    setModeState(draftMode(draftProjectId, draftThreadKey))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftProjectId, draftThreadKey])

  // The floating "Add to chat" menu over a reply writes straight to the draft
  // store; pick the new selection up and put the caret back in the input.
  useEffect(() => {
    if (!draftProjectId || !draftThreadKey) return
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<ChatDraftRestoredDetail>).detail
      if (detail?.projectId !== draftProjectId || detail?.threadKey !== draftThreadKey) return
      setSelections(parseDraftSelections(getChatDraftSelections(draftProjectId, draftThreadKey)))
      textareaRef.current?.focus()
    }
    window.addEventListener(CHAT_DRAFT_SELECTIONS_EVENT, onChanged)
    return () => window.removeEventListener(CHAT_DRAFT_SELECTIONS_EVENT, onChanged)
  }, [draftProjectId, draftThreadKey])

  // A send the server refused (out of credits) writes the text back to the
  // draft store from outside; re-read it so nothing the user typed is lost.
  useEffect(() => {
    if (!draftProjectId || !draftThreadKey) return
    const onRestored = (event: Event) => {
      const detail = (event as CustomEvent<ChatDraftRestoredDetail>).detail
      if (detail?.projectId !== draftProjectId || detail?.threadKey !== draftThreadKey) return
      setValue(getChatDraft(draftProjectId, draftThreadKey))
      setFiles(
        getChatDraftAttachments<ChatAttachment>(draftProjectId, draftThreadKey).map(
          draftAttachmentToFile
        )
      )
      setSelections(parseDraftSelections(getChatDraftSelections(draftProjectId, draftThreadKey)))
    }
    window.addEventListener(CHAT_DRAFT_RESTORED_EVENT, onRestored)
    return () => window.removeEventListener(CHAT_DRAFT_RESTORED_EVENT, onRestored)
  }, [draftProjectId, draftThreadKey])

  // Auto-grow the textarea with its content (capped by CSS max-height).
  // Grow with the text up to a cap, then scroll inside the textarea.
  const isMobile = useIsMobile()
  const maxLines = isMobile ? MOBILE_MAX_LINES : MAX_LINES
  const maxHeight = maxLines * LINE_HEIGHT_PX + TEXTAREA_PADDING_PX
  // An empty textarea's scrollHeight includes its (wrapped) placeholder, so a
  // measurement taken before the layout has settled — first paint of a
  // thread, a narrow column that is still opening — sticks at two lines
  // until the next keystroke. Re-measure on every width change and once the
  // fonts are in, not only when the value changes.
  const fitToContent = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = '0px'
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
  }, [maxHeight])
  useEffect(() => {
    fitToContent()
  }, [value, fitToContent])
  useEffect(() => {
    const el = textareaRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    let width = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return
      width = el.clientWidth
      fitToContent()
    })
    observer.observe(el)
    if (typeof document !== 'undefined' && 'fonts' in document) {
      void document.fonts.ready.then(fitToContent)
    }
    return () => observer.disconnect()
  }, [fitToContent])

  // Touch keyboards have no Shift+Enter: there Enter stays a newline.
  const [coarsePointer, setCoarsePointer] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  )
  useEffect(() => {
    const media = window.matchMedia('(pointer: coarse)')
    const onChange = () => setCoarsePointer(media.matches)
    media.addEventListener('change', onChange)
    onChange()
    return () => media.removeEventListener('change', onChange)
  }, [])
  const plainEnterSends = enterSends({ phone: isMobile, coarsePointer })

  const isUploading = files.some((f) => f.status === 'uploading')
  const canSend = value.trim().length > 0 && !disabled && !isUploading
  // Stop control while the agent works (separate from the dictation stop,
  // which lives in the voice row). Pending until the server settles the turn
  // or the status flips on its own.
  const [stopping, setStopping] = useState(false)
  const [confirmingStop, setConfirmingStop] = useState(false)
  const showStop = busy && !!onStop
  useEffect(() => {
    if (!busy) {
      setStopping(false)
      setConfirmingStop(false)
    }
  }, [busy])
  const runStop = async () => {
    if (!onStop || stopping) return
    setConfirmingStop(false)
    setStopping(true)
    try {
      await onStop()
    } catch (err) {
      console.error('[composer] stop failed:', err)
      toast.error(t('composer.stopFailed'))
    } finally {
      // Re-arm either way: the server answered, so if the thread is somehow
      // still busy the user can press again.
      setStopping(false)
    }
  }
  // A long run is stopped on purpose or not at all: ask first. A young run
  // stops instantly — there the press is nearly always a correction.
  const stop = async () => {
    if (!onStop || stopping) return
    if (shouldConfirmStop(busySince)) {
      setConfirmingStop(true)
      return
    }
    await runStop()
  }

  const uploadFile = useCallback(
    (file: File) => {
      if (!projectId) return
      const id = crypto.randomUUID()
      mutateFiles((prev) => [
        ...prev,
        {
          id,
          name: file.name,
          size: file.size,
          contentType: file.type || 'application/octet-stream',
          progress: 0,
          status: 'uploading',
        },
      ])
      const patch = (p: Partial<UploadingFile>) =>
        mutateFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...p } : f)))

      const formData = new FormData()
      formData.append('file', file)
      const xhr = new XMLHttpRequest()
      xhrs.current.set(id, xhr)
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) patch({ progress: Math.round((e.loaded / e.total) * 100) })
      }
      xhr.onload = () => {
        xhrs.current.delete(id)
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const r = JSON.parse(xhr.responseText) as {
              url: string
              filename: string
              size: number
              contentType: string
              /** Set when the upload became a project document (converted for the agent). */
              documentId?: string
              agentFilename?: string
            }
            patch({
              status: 'uploaded',
              progress: 100,
              result: {
                id,
                url: r.url,
                filename: r.filename,
                size: r.size,
                contentType: r.contentType,
                ...(r.documentId ? { documentId: r.documentId } : {}),
                ...(r.agentFilename ? { agentFilename: r.agentFilename } : {}),
              },
            })
          } catch {
            patch({ status: 'error', error: t('composer.invalidResponse') })
          }
        } else {
          patch({ status: 'error', error: t('composer.uploadFailed', { status: xhr.status }) })
        }
      }
      xhr.onerror = () => {
        xhrs.current.delete(id)
        patch({ status: 'error', error: t('composer.networkError') })
      }
      xhr.onabort = () => xhrs.current.delete(id)
      xhr.open('POST', `/workspaces/${projectId}/uploads`)
      xhr.withCredentials = true
      xhr.send(formData)
    },
    [projectId, t]
  )

  const removeFile = (id: string) => {
    xhrs.current.get(id)?.abort()
    xhrs.current.delete(id)
    mutateFiles((prev) => prev.filter((f) => f.id !== id))
  }

  const handleDocumentsSelected = useCallback((documents: PickedDocument[]) => {
    mutateFiles((prev) => {
      const existing = new Set(prev.map((f) => f.id))
      const added: UploadingFile[] = documents
        .filter((doc) => !existing.has(doc.id))
        .map((doc) => ({
          id: doc.id,
          name: doc.displayName,
          size: 0,
          contentType: doc.contentType,
          progress: 100,
          status: 'uploaded',
          result: {
            id: doc.id,
            url: doc.url,
            filename: doc.displayName,
            size: 0,
            contentType: doc.contentType,
            documentId: doc.id,
            agentFilename: doc.filename,
          },
        }))
      return [...prev, ...added]
    })
  }, [])

  // A pasted screenshot or a file copied from Finder becomes an attachment
  // through the same path as drag-and-drop and the file picker. When the
  // clipboard holds files the default paste is suppressed: for a copied file
  // the browser would otherwise insert its name (or path) as text. A paste
  // without files (plain text, rich text, a URL) is left to the textarea.
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!projectId) return
    const pasted = clipboardFiles(e.clipboardData)
    if (pasted.length === 0) return
    e.preventDefault()
    for (const file of pasted) uploadFile(file)
  }

  /**
   * Send `text` with the current attachments and selections (quoted above the
   * text, see quoteSelections); false when sending is not possible.
   */
  const submitText = (text: string): boolean => {
    if (!text.trim() || disabled || isUploading) return false
    const uploaded = files.filter((f) => f.status === 'uploaded' && f.result)
    const attachments = uploaded.map((f) => f.result as ChatAttachment)
    const content = quoteSelections(selections, text)
    const sent = onSend(content, attachments.length > 0 ? attachments : undefined)
    const restore = files
    const restoreSelections = selections
    setValue('')
    setFiles([])
    setSelections([])
    onTyping?.(false)
    if (draftKey) clearChatDraft(draftKey.projectId, draftKey.threadKey)
    // A refused send (out of credits) must not eat the draft.
    void Promise.resolve(sent).then((ok) => {
      if (ok !== false) return
      setValue(text)
      setFiles(restore)
      setSelections(restoreSelections)
      if (draftKey) {
        setChatDraft(draftKey.projectId, draftKey.threadKey, text)
        setChatDraftSelections(draftKey.projectId, draftKey.threadKey, restoreSelections)
      }
    })
    return true
  }
  const submitTextRef = useRef(submitText)
  submitTextRef.current = submitText
  // Dictation → message is one hand-off, shared with the silence auto-stop.
  // Created once so the in-flight token survives re-renders; `submitText` and
  // the draft key are read fresh at hand-off time.
  const dictationSubmit = (dictationSubmitRef.current ??= createDictationSubmit({
    stop: () => dictationStopRef.current(),
    base: () => dictationBaseRef.current,
    reset: () => {
      dictationBaseRef.current = ''
    },
    send: (text) => submitTextRef.current(text),
    keep: (text) => {
      setValue(text)
      const dk = draftKeyRef.current
      if (dk) setChatDraft(dk.projectId, dk.threadKey, text)
      textareaRef.current?.focus()
    },
  }))
  const submit = () => {
    // Pressing submit while the mic is on ends the session first: awaiting the
    // stop puts the utterance that was still in the air into the message, and
    // nothing is left listening (or billed) afterwards. `stopping` is the
    // window where the visuals are already idle but the flush is not done.
    if (dictation.active || dictation.stopping || dictationSubmit.pending()) {
      void dictationSubmit.run()
      return
    }
    if (canSend) submitText(value)
  }

  /**
   * Desktop: Enter sends, Shift+Enter (or Alt+Enter) inserts a newline.
   * Touch keyboards have no Shift+Enter, so there Enter is always a newline
   * and the message goes out with the send button. ⌘/Ctrl+Enter sends
   * everywhere — both modifiers are accepted whatever the platform; only
   * SEND_SHORTCUT's hint follows it.
   */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return
    const modified = e.metaKey || e.ctrlKey
    if (!modified && !(plainEnterSends && !e.shiftKey && !e.altKey)) return
    e.preventDefault()
    submit()
  }

  /** Send button hint + aria-label — names the keyboard shortcut for this platform. */
  const sendLabel = showStop
    ? t('composer.stopGenerating')
    : t('composer.sendWithShortcut', { shortcut: plainEnterSends ? 'Enter' : SEND_SHORTCUT.label })

  const chips: (ComposerAttachment & { id: string })[] = files.map((f) => ({
    id: f.id,
    name: f.name,
    meta:
      f.status === 'error'
        ? (f.error ?? t('composer.error'))
        : f.result?.documentId
          ? t('composer.projectDocument')
          : f.size > 0
            ? formatFileSize(f.size)
            : t('composer.file'),
    state: f.status === 'uploaded' ? 'done' : f.status,
    progress: f.progress,
    kind: attachmentKind(f.contentType),
  }))

  const draftMode = (projectIdKey?: string, threadKey?: string): ChatMode => {
    if (resetModeToNormal || !projectIdKey || !threadKey) return 'normal'
    const raw = getChatDraftMode(projectIdKey, threadKey)
    return isChatMode(raw) ? raw : 'normal'
  }
  const [mode, setModeState] = useState<ChatMode>(() =>
    draftMode(draftKey?.projectId, draftKey?.threadKey)
  )
  const changeMode = (next: ChatMode) => {
    if (draftKey) setChatDraftMode(draftKey.projectId, draftKey.threadKey, next)
    setModeState(next)
  }

  return (
    <Composer className={cn('max-w-none', className)}>
      {mode === 'max' && (
        <p className="mb-1.5 flex items-center justify-center gap-1.5 px-3 text-center text-xs text-amber-600 dark:text-amber-400">
          <TriangleAlert className="size-3.5 shrink-0" />
          {t('mode.maxWarning')}
        </p>
      )}
      <ComposerBar
        dragActive={dragActive}
        className="gap-1 rounded-[26px] p-1.5 shadow-lg shadow-black/5 dark:border-white/15 dark:bg-white/[0.07] dark:shadow-black/40"
        onDragOver={(e) => {
          e.preventDefault()
          if (projectId) setDragActive(true)
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragActive(false)
          for (const file of Array.from(e.dataTransfer.files)) uploadFile(file)
        }}
      >
        {(chips.length > 0 || selections.length > 0) && (
          <ComposerAttachments className="px-2 pt-1">
            {selections.length > 0 && (
              <SelectionsChip
                selections={selections}
                onRemove={(id) => mutateSelections((prev) => prev.filter((s) => s.id !== id))}
              />
            )}
            {chips.map((chip) => (
              <ComposerAttachmentChip
                key={chip.id}
                attachment={chip}
                onRemove={() => removeFile(chip.id)}
              />
            ))}
          </ComposerAttachments>
        )}
        <div className="flex items-end gap-1">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              for (const file of Array.from(e.target.files ?? [])) uploadFile(file)
              e.target.value = ''
            }}
            aria-label={t('composer.attachFile')}
          />
          {projectId ? (
            <AttachmentSourcePicker
              projectId={projectId}
              onUploadFromComputer={() => fileInputRef.current?.click()}
              onDocumentsSelected={handleDocumentsSelected}
              attachedDocIds={files.filter((f) => f.status === 'uploaded').map((f) => f.id)}
            >
              <button
                type="button"
                aria-label={t('composer.attach')}
                className={cn(ghostButton, 'mb-0.5 size-9 shrink-0 rounded-full')}
              >
                <Plus className="size-4" />
              </button>
            </AttachmentSourcePicker>
          ) : (
            <span className="size-9 shrink-0" />
          )}
          <textarea
            ref={textareaRef}
            data-slot="composer-input"
            value={value}
            rows={1}
            autoFocus={autoFocus}
            placeholder={
              busy ? t('composer.placeholderBusy') : (placeholder ?? t('composer.placeholder'))
            }
            disabled={disabled}
            onChange={(e) => {
              const next = e.target.value
              setValue(next)
              onTyping?.(next.length > 0)
              if (draftKey) setChatDraft(draftKey.projectId, draftKey.threadKey, next)
            }}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            style={{ maxHeight }}
            className="placeholder:text-foreground/35 min-h-9 w-full flex-1 resize-none bg-transparent px-1 py-2 text-[15px] leading-6 caret-blue-500 outline-none dark:caret-blue-400"
          />
          {dictationEnabled && !dictation.active && (
            <ComposerVoiceButton
              active={false}
              disabled={disabled}
              onClick={() => void startDictation()}
              className="mb-0.5 shrink-0"
            />
          )}
          <TooltipProvider delayDuration={400}>
            <Tooltip>
              <TooltipTrigger asChild>
                <ComposerSend
                  // TooltipTrigger's Slot would otherwise stamp its own data-slot on the button.
                  data-slot="composer-send"
                  streaming={showStop}
                  idle={canSend}
                  disabled={showStop ? stopping : !canSend}
                  aria-busy={stopping || undefined}
                  aria-label={sendLabel}
                  onClick={showStop ? () => void stop() : submit}
                  countdownUntil={
                    dictation.status === 'recording' && dictation.countingDown && value.trim()
                      ? dictation.autoStopAt
                      : null
                  }
                  className="mb-0.5 shrink-0"
                />
              </TooltipTrigger>
              <TooltipContent side="top">{sendLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {dictation.active && (
          <ComposerVoice
            recording={dictation.status === 'recording'}
            seconds={dictation.seconds}
            onStop={() => void stopDictation()}
            className="min-h-8 ps-3 pb-1"
          />
        )}
      </ComposerBar>
      {/* Mode selector — Claude-style quiet control under the input. */}
      <div className="flex flex-col items-end gap-1 pt-1 pr-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="text-foreground/45 hover:text-foreground/80 flex items-center gap-1 text-[11.5px] transition-colors outline-none"
            >
              {t('mode.label')}: {t(`mode.${mode}`)}
              <ChevronDown className="size-3 opacity-70" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuRadioGroup
              value={mode}
              onValueChange={(value) => changeMode(value as ChatMode)}
            >
              {(['normal', 'high', 'max'] as const).map((m) => (
                <DropdownMenuRadioItem key={m} value={m}>
                  <span className="flex-1">{t(`mode.${m}`)}</span>
                  <ModeBar mode={m} />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {mode !== 'normal' && <ModeBar mode={mode} />}
      </div>

      {/* Stopping a long run throws the answer away — ask before doing it. */}
      <Dialog open={confirmingStop} onOpenChange={(open) => !open && setConfirmingStop(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('composer.stopConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('composer.stopConfirmBody')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmingStop(false)}>
              {t('composer.stopConfirmKeepWaiting')}
            </Button>
            <Button variant="destructive" onClick={() => void runStop()}>
              {t('composer.stopConfirmStop')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Composer>
  )
}
